import * as React from 'react';
import { useTranslations } from 'next-intl';
import { styled } from '@unity/cloud-ui/styles';
import { Typography, IconButton } from '@unity/cloud-ui';
import { LinkSimpleHorizontal } from '@phosphor-icons/react';

import { getAnchorLink } from '@/utils/get-anchor-link';

import { Link } from '../link';

export const CUSTOM_HEADING_IDENTIFIER = '##';

const StyledTypography = styled(Typography)(() => ({
  position: 'relative',
  '[data-anchor]': {
    position: 'absolute',
    left: -25,
    opacity: 0,
    scrollMarginTop: 50,
  },

  '&:hover': {
    '[data-anchor]': {
      opacity: 1,
    },
  },
}));

const getInnerText = (node: React.ReactNode) => {
  if (typeof node === 'string') {
    return node;
  }

  if (Array.isArray(node)) {
    return node.map(getInnerText).join('');
  }

  if (!React.isValidElement(node)) {
    return '';
  }

  return getInnerText((node as React.JSX.Element).props.children);
};

function parseCustomHeading(props: { id: string | undefined; children: React.ReactNode }) {
  const id = props.id;
  const headingInnerText = getInnerText(props.children);

  const [label, customId] = headingInnerText.split(CUSTOM_HEADING_IDENTIFIER);

  if (customId) {
    return { anchor: customId, children: label, label };
  }

  if (!id) {
    return { anchor: getAnchorLink(headingInnerText), children: props.children, label };
  }

  return { anchor: id, children: props.children };
}

export const Header = (props) => {
  const t = useTranslations();
  const { children: _, ...rest } = props;

  const { anchor, children, label } = parseCustomHeading(props);

  return (
    <StyledTypography {...rest}>
      <IconButton
        data-anchor={anchor}
        aria-label={t('mdx:header:anchor', { title: label })}
        LinkComponent={Link}
        href={`#${anchor}`}
        outlined
        color="default"
        size="extraSmall"
      >
        <LinkSimpleHorizontal />
      </IconButton>
      {children}
    </StyledTypography>
  );
};
